import React from 'react';
import type { EnterpriseClientLogoConfig } from '../../themes/themeTypes';

interface ClientLogoStripProps {
  logos?: EnterpriseClientLogoConfig[];
  label?: string;
}

/**
 * Grayscale client-logo strip for B2B trust signaling.
 * If no logos are configured, renders a subtle placeholder line with the label.
 */
const ClientLogoStrip: React.FC<ClientLogoStripProps> = ({
  logos,
  label = 'Trusted by engineering teams at',
}) => {
  if (!logos || logos.length === 0) {
    // Render label-only placeholder so the section still anchors the page
    return (
      <section
        className="bg-[var(--color-bg)] border-b border-border-light"
        aria-label="Client logos"
        data-testid="client-logo-strip"
      >
        <div
          className="container mx-auto px-lg py-xl text-center"
          style={{ maxWidth: 'var(--container-max-width)' }}
        >
          <p className="text-xs uppercase tracking-wider text-text-light">
            {label}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      className="bg-[var(--color-bg)] border-b border-border-light"
      aria-label="Client logos"
      data-testid="client-logo-strip"
    >
      <div
        className="container mx-auto px-lg py-xl"
        style={{ maxWidth: 'var(--container-max-width)' }}
      >
        <p className="text-xs uppercase tracking-wider text-text-light text-center mb-md">
          {label}
        </p>
        <ul className="flex flex-wrap items-center justify-center gap-2xl list-none p-0 m-0">
          {logos.map((logo) => (
            <li key={logo.name} className="flex items-center">
              {logo.imageUrl ? (
                <img
                  src={logo.imageUrl}
                  alt={logo.name}
                  data-testid="client-logo"
                  // Greyscale + reduced opacity by default, full colour on
                  // hover — a restrained trust-signal treatment for image
                  // logos. Transition speed honours the global
                  // prefers-reduced-motion rule.
                  className="h-8 w-auto grayscale opacity-40 hover:opacity-100 hover:grayscale-0 transition-all"
                />
              ) : (
                <span
                  data-testid="client-logo"
                  className="text-lg font-semibold text-text-light tracking-wide"
                >
                  {logo.name}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default ClientLogoStrip;
